import {
  parseRecordingConsentRequired,
  RECORDING_CONSENT_REQUIRED_PEER,
  RECORDING_CONSENT_REQUIRED_SELF,
  recordingConsentRequiredPeerMessage,
  type ParsedRecordingConsentRequired,
} from '@shared/calls/recording-consent'
import type { ChatUser } from '../../../types/chat'

/**
 * Calls need the member's recording consent BEFORE they connect: the server
 * refuses the INVITE with a typed rejection (see `assertInviteRecordingConsent`
 * in publish-voice-call-signal). These helpers turn the error text the client
 * receives into a typed result, and give a started call that was refused for
 * the OTHER party's consent a throwable form so callers that already treat a
 * failed `startCall` as a failed attempt (telehealth "Start call") keep working.
 */

export type RecordingConsentRejection = ParsedRecordingConsentRequired

/** Reads the typed rejection out of whatever a failed INVITE threw. */
export function recordingConsentRejectionFromError(err: unknown): RecordingConsentRejection | null {
  const name = err instanceof Error ? err.name : ''
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  const parsed = parseRecordingConsentRequired(message)
  if (parsed) return parsed
  // `errorType` can carry the code even when a transport drops the tag.
  if (name === RECORDING_CONSENT_REQUIRED_SELF) return { code: RECORDING_CONSENT_REQUIRED_SELF }
  if (name === RECORDING_CONSENT_REQUIRED_PEER) {
    return { code: RECORDING_CONSENT_REQUIRED_PEER, firstName: '' }
  }
  return null
}

/** Thrown from `startCall` after the peer notice was shown (the call was not placed). */
export class PeerRecordingConsentRequiredError extends Error {
  readonly firstName: string
  constructor(firstName: string) {
    super(recordingConsentRequiredPeerMessage(firstName))
    this.name = RECORDING_CONSENT_REQUIRED_PEER
    this.firstName = firstName
  }
}

/** First name for copy and the message draft — never a surname. */
export function consentFirstName(user: Pick<ChatUser, 'givenName' | 'displayName'>): string {
  const name = (user.givenName ?? '').trim() || (user.displayName ?? '').trim()
  return name.split(/\s+/)[0] ?? ''
}
